import { t } from "@lingui/core/macro";
import type { BillingDepleted, MeterDimension, MeterUnit } from "@orbin/contracts";
import {
  AI_DATA_SHARING_DENIED_CODE,
  BillingDepletedSchema,
  meterUnitsInMinutes,
  PAYER_IDENTIFICATION_REQUIRED_CODE,
} from "@orbin/contracts";

/** The recharge state a refused action carries (`data.depleted` on a CONFLICT error), if any. */
export function depletedFromError(error: unknown): BillingDepleted | null {
  if (!error || typeof error !== "object") return null;
  if ((error as { code?: unknown }).code !== "CONFLICT") return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return null;
  const parsed = BillingDepletedSchema.safeParse((data as { depleted?: unknown }).depleted);
  return parsed.success ? parsed.data : null;
}

export type SharingRecipient = { key: string; name: string };

/** The recipients a typed AI data-sharing refusal names, or null for any other error. */
export function sharingDeniedFromError(error: unknown): SharingRecipient[] | null {
  if (!error || typeof error !== "object") return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return null;
  if ((data as { code?: unknown }).code !== AI_DATA_SHARING_DENIED_CODE) return null;
  const recipients = (data as { recipients?: unknown }).recipients;
  if (!Array.isArray(recipients)) return [];
  return recipients.flatMap((entry) =>
    entry && typeof entry.key === "string" && typeof entry.name === "string"
      ? [{ key: entry.key, name: entry.name }]
      : [],
  );
}

export function dimensionLabel(dimension: MeterDimension): string {
  if (dimension === "task_credits") return t`Task credits`;
  if (dimension === "computer_minutes") return t`Computer time`;
  return t`Voice time`;
}

/**
 * The counts of a limit that is not an amount of metered work, e.g. how many cloud computers
 * are up. Null when the refusal is about a dimension that ran out.
 */
export function depletedLimit(
  depleted: BillingDepleted,
): { kind: "concurrent_cloud_computers" | "cloud_capacity"; active: number; max: number } | null {
  const limit = depleted.limit;
  if (!limit) return null;
  return { kind: limit.kind, active: limit.active, max: limit.max };
}

/** Renders a quantity in the unit it is counted in: credits for micro-USD, minutes for seconds. */
export function formatMeterUnits(unit: MeterUnit, units: number, locale: string): string {
  if (unit === "micro_usd") {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(units / 1_000_000);
  }
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "minute",
    unitDisplay: "short",
    maximumFractionDigits: 0,
  }).format(meterUnitsInMinutes(units));
}

/** BRL from centavos, e.g. "R$ 25,00". */
export function formatBrl(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: "BRL" }).format(cents / 100);
}

/** Share of the allowance used, between 0 and 1; 0 when there is no cap. */
export function quotaFill(usedUnits: number, limitUnits: number | null): number {
  if (!limitUnits) return 0;
  return Math.min(1, usedUnits / limitUnits);
}

/** PIX top-up sizes in centavos. */
export const PIX_AMOUNTS_CENTS = [2500, 5000, 10000, 25000] as const;

/** A typed refusal the composer turns into an in-context step instead of a generic error. */
export type SendAction =
  | { kind: "depleted"; depleted: BillingDepleted }
  | { kind: "sharing"; recipients: SharingRecipient[] };

export function sendActionFromError(error: unknown): SendAction | null {
  const depleted = depletedFromError(error);
  if (depleted) return { kind: "depleted", depleted };
  const recipients = sharingDeniedFromError(error);
  return recipients ? { kind: "sharing", recipients } : null;
}

/**
 * Whether a refused charge is asking for the payer's CPF or CNPJ. The tax id is only requested
 * then, so a payer who never needs it never sees the field. The typed code is the only signal:
 * the wording of an error is not a contract.
 */
export function needsPayerIdentification(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const data = (error as { data?: { code?: unknown } }).data;
  return data?.code === PAYER_IDENTIFICATION_REQUIRED_CODE;
}
